<script lang="ts">
    import { hapticError } from "$lib/haptics";
    import type { Optional } from "$lib/types/generic";
    import type { TitEmotions } from "$lib/types/tit";
    import type { DialogButton, SmallDialogIcons } from "$lib/types/dialog";
    import { dialogInputValue } from "$lib/state/dialog-input";

    import DialogContainer from "$components/dialog/DialogContainer.svelte";

    import Tit from "$components/misc/Tit.svelte";
    import DialogButtons from "$components/dialog/DialogButtons.svelte";

    import IconAlertTriangle from "@tabler/icons-svelte/IconAlertTriangle.svelte";

    export let id: string;
    export let mascot: Optional<TitEmotions> = undefined;
    export let icon: Optional<SmallDialogIcons> = undefined;
    export let title = "";
    export let bodyText = "";
    export let bodySubText = "";
    export let input: Optional<{ placeholder?: string, type?: string }> = undefined;
    export let buttons: Optional<DialogButton[]> = undefined;
    export let dismissable = true;
    export let leftAligned = false;

    let close: () => void;

    // error mascot art is not used in dialogs unless it's an error
    if (mascot === "error") {
        setTimeout(() => {
            hapticError();
        }, 150)
    }
</script>

<DialogContainer {id} {dismissable} bind:close>
    <div
        class="dialog-body small-dialog"
        class:mascot-visible={mascot}
        class:align-left={leftAligned}
    >
        {#if mascot}
            <div class="mascot-container">
                <Tit
                    emotion={mascot}
                    forceLoaded={id === 'nojs-dialog'}
                />
            </div>
        {/if}
        <div class="dialog-inner-container">
            {#if title || icon}
                <div class="popup-header">
                    {#if icon === "warn-red"}
                        <div class="popup-icon {icon}">
                            <IconAlertTriangle />
                        </div>
                    {/if}
                    {#if title}
                        <h2 class="popup-title" tabindex="-1">{title}</h2>
                    {/if}
                </div>
            {/if}
            {#if bodyText}
                <div class="body-text" tabindex="-1">{bodyText}</div>
            {/if}
            {#if bodySubText}
                <div class="subtext popup-subtext">{bodySubText}</div>
            {/if}
            {#if input}
                <input
                    class="dialog-input"
                    type={input.type || "text"}
                    placeholder={input.placeholder}
                    bind:value={$dialogInputValue}
                />
            {/if}
        </div>
        {#if buttons}
            <DialogButtons {buttons} closeFunc={close} />
        {/if}
    </div>
</DialogContainer>

<style>
    .small-dialog,
    .dialog-inner-container {
        display: flex;
        flex-direction: column;
        gap: var(--padding);
    }

    .dialog-inner-container {
        overflow-y: scroll;
        gap: 8px;
    }

    .small-dialog {
        text-align: center;
        max-width: 340px;
        width: calc(100% - var(--padding) - var(--dialog-padding) * 2);
        max-height: 85%;
        margin: calc(var(--padding) / 2);
    }

    .small-dialog.mascot-visible {
        padding-top: calc(var(--padding) * 4);
    }

    .mascot-container {
        position: absolute;
        top: -120px;
    }

    .popup-title {
        color: var(--secondary);
        font-size: 19px;
    }

    .popup-header,
    .popup-icon {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .popup-icon :global(svg) {
        stroke-width: 1.5px;
        height: 50px;
        width: 50px;
    }

    .warn-red :global(svg) {
        stroke: var(--red);
    }

    .body-text {
        font-size: 14.5px;
        font-weight: 500;
        line-height: 1.7;
        color: var(--gray);
        white-space: pre-wrap;
        user-select: text;
        -webkit-user-select: text;
    }

    .popup-subtext {
        opacity: 0.7;
        padding: 0;
    }

    .align-left .body-text {
        text-align: start;
    }

    .align-left .popup-header {
        align-items: start;
        gap: 2px;
    }

    .align-left .popup-icon :global(svg) {
        height: 40px;
        width: 40px;
        stroke-width: 1.8px;
    }

    .dialog-input {
        width: 100%;
        box-sizing: border-box;
        padding: 10px 12px;
        font-size: 14px;
        font-weight: 500;
        color: var(--secondary);
        background: var(--button);
        border: 1.5px solid var(--popup-stroke);
        border-radius: var(--border-radius);
        outline: none;
    }

    .dialog-input:focus {
        border-color: var(--accent);
    }
</style>
